import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { QrCode, MessageCircle, Upload, FileSpreadsheet, Send, Trash, Plus, Check, X } from 'lucide-react';
import api from '../api';
import { PageHeader } from '../components/common';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../lib/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

export default function WhatsAppManager() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [status, setStatus] = useState('disconnected'); // disconnected, connecting, connected
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyers, setSelectedBuyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingProgress, setSendingProgress] = useState({ current: 0, total: 0 });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');
  const fileInputRef = useRef(null);

  // Referência para o intervalo de verificação de status
  const statusCheckIntervalRef = useRef(null);
  
  // Referência para o timeout de conexão
  const connectTimeoutRef = useRef(null);
  
  // Referência para o status atual (evita problemas de closure)
  const statusRef = useRef(status);
  
  // Atualizar a ref sempre que o status mudar
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // Função para iniciar o polling de status de conexão com frequência ajustável
  const startStatusPolling = useCallback((frequency = 3000) => {
    // Limpar intervalo anterior se existir
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
      console.log('Polling anterior interrompido');
    }
    
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] Iniciando polling com frequência: ${frequency}ms`);
    
    // Para a primeira verificação, adicionar um pequeno delay para garantir que o backend teve tempo de inicializar
    setTimeout(() => {
      // Verificar status imediatamente antes de iniciar o intervalo
      checkConnectionStatus();
      
      // Iniciar novo intervalo para verificar status com a frequência especificada
      statusCheckIntervalRef.current = setInterval(() => {
        // Verificar se já não está conectado para evitar polling desnecessário
        if (statusRef.current === 'connected') {
          console.log('Detectado status conectado durante polling, interrompendo');
          stopStatusPolling();
          return;
        }
        
        checkConnectionStatus();
      }, frequency);
    }, 1000); // Aguardar 1 segundo antes da primeira verificação
    
    // Sempre definir um timeout máximo para o polling (2 minutos)
    setTimeout(() => {
      if (statusCheckIntervalRef.current) {
        console.log('Tempo máximo de polling atingido (2 minutos), interrompendo');
        stopStatusPolling();
      }
    }, 120000);
  }, [status]);

  // Função para parar o polling de status
  const stopStatusPolling = useCallback(() => {
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
  }, []);

  // Limpar o intervalo quando o componente for desmontado
  useEffect(() => {
    return () => {
      // Limpar o intervalo de verificação de status
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }
      
      // Limpar o timeout de conexão
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      
      // Limpar o contador de falhas
      window.whatsappFailCount = 0;
    };
  }, []);

  // Carregar compradores e verificar status inicial
  useEffect(() => {
    let mounted = true;
    let initialStatusCheckTimeout = null;
    
    loadBuyers();
    
    // Verificar o status inicial com tentativas em caso de falha
    const checkInitialStatus = async (retryCount = 0) => {
      try {
        if (!mounted) return;
        
        console.log('Verificando status inicial do WhatsApp...');
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await api.get('/whatsapp/status', { 
          signal: controller.signal,
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        
        clearTimeout(timeoutId);
        
        if (!mounted) return;

        console.log('Status inicial do WhatsApp:', response.data);
        
        if (response.data.connected) {
          // Se já está conectado, atualizar o estado e não iniciar polling
          console.log('WhatsApp já está conectado na inicialização');
          setStatus('connected');
          setQrCodeImage(''); // Limpar QR code anterior se houver
        } else {
          // Se não está conectado, atualizar o estado como desconectado
          console.log('WhatsApp não está conectado na inicialização');
          setStatus('disconnected');
        }
      } catch (error) {
        if (!mounted) return;
        
        console.error('Erro ao verificar status inicial:', error);
        
        // Tentar novamente após um curto delay (até 3 tentativas)
        if (retryCount < 3) {
          console.log(`Tentando verificar status novamente (${retryCount + 1}/3)...`);
          initialStatusCheckTimeout = setTimeout(() => {
            checkInitialStatus(retryCount + 1);
          }, 2000);
        } else {
          setStatus('disconnected');
          console.error('Falha ao verificar status inicial após várias tentativas');
        }
      }
    };
    
    checkInitialStatus();
    
    return () => {
      mounted = false;
      if (initialStatusCheckTimeout) {
        clearTimeout(initialStatusCheckTimeout);
      }
    };
  }, []);

  const loadBuyers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/compradores');
      
      // Filtrar apenas compradores com celular no contato principal
      const buyersWithPhone = response.data.filter(
        buyer => buyer.contatoPrincipal?.celular || buyer.contatoPrincipal?.telefone
      );
      
      setBuyers(buyersWithPhone);
    } catch (error) {
      console.error('Erro ao carregar compradores:', error);
      toast({
        variant: "destructive",
        title: t('whatsapp.errors.loadBuyers.title'),
        description: t('whatsapp.errors.loadBuyers.description')
      });
    } finally {
      setLoading(false);
    }
  };

  const checkConnectionStatus = useCallback(async () => {
    try {
      const currentStatus = status; // Capturar o status atual para comparação
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] Verificando status de conexão WhatsApp (status atual: ${currentStatus})...`);
      
      // Usar timeout para evitar problemas de conexão pendente
      const statusResponse = await api.get('/whatsapp/status', { 
        timeout: 10000, // Aumentado para 10 segundos
        // Prevenir cache do navegador
        headers: { 
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log(`[${timestamp}] Status atual do backend:`, statusResponse.data);
      
      // Resetar contador de falhas após sucesso
      window.whatsappFailCount = 0;
      
      // Se o backend diz que está conectado
      if (statusResponse.data.connected) {
        // Verificar se o estado da validação também confirma a conexão
        const isReallyConnected = statusResponse.data.validationStatus?.valid !== false;
        
        if (isReallyConnected) {
          if (currentStatus !== 'connected') {
            // O backend detecta como conectado mas o frontend ainda não atualizou
            console.log(`[${timestamp}] ✅ CONEXÃO DETECTADA: Atualizando status para connected`);
            setStatus('connected');
            
            // Quando conectar com sucesso, parar o polling completamente
            stopStatusPolling();
            
            // Limpar o QR code quando conectar
            setQrCodeImage('');
            
            // Mostrar notificação de conexão bem-sucedida apenas se estava conectando
            if (currentStatus === 'connecting') {
              toast({
                title: t('whatsapp.messages.connected.title'),
                description: t('whatsapp.messages.connected.description')
              });
            }
          }
          else {
            console.log(`[${timestamp}] WhatsApp continua conectado`);
            // Apenas manter o status, mas parar qualquer polling que ainda esteja ativo
            stopStatusPolling();
          }
        } else {
          // O backend diz que está conectado mas a validação falhou
          console.log(`[${timestamp}] Backend diz que está conectado, mas validação falhou: ${statusResponse.data.validationStatus?.message}`);
          
          if (currentStatus === 'connected') {
            // Se já estava marcado como conectado, mas agora detectamos inconsistência
            console.log(`[${timestamp}] Detectada inconsistência no estado de conexão, atualizando para disconnected`);
            setStatus('disconnected');
            stopStatusPolling();
            
            toast({
              variant: "warning",
              title: "Estado inconsistente",
              description: "A conexão com o WhatsApp está em estado inconsistente. Tente conectar novamente."
            });
          }
        }
      } 
      // Se estava conectado mas agora está desconectado
      else if (!statusResponse.data.connected && currentStatus === 'connected') {
        console.log(`[${timestamp}] WhatsApp desconectado: Atualizando status`);
        setStatus('disconnected');
        stopStatusPolling(); // Parar o polling quando desconectado
        
        toast({
          variant: "destructive",
          title: t('whatsapp.messages.disconnected.title'),
          description: t('whatsapp.messages.disconnected.description')
        });
      }
      // Se estava conectando mas ainda não está conectado
      else if (!statusResponse.data.connected && currentStatus === 'connecting') {
        // Verificar se tem QR code
        if (statusResponse.data.qrCode && statusResponse.data.qrCode !== qrCodeImage) {
          console.log(`[${timestamp}] Novo QR code recebido durante conexão`);
          setQrCodeImage(statusResponse.data.qrCode);
          
          // Toast para informar sobre o QR Code
          if (!qrCodeImage) { // Somente mostrar na primeira vez
            toast({
              title: t('whatsapp.messages.qrAvailable.title'),
              description: t('whatsapp.messages.qrAvailable.description')
            });
          }
        }
        // Manter o polling ativo durante a conexão
      }
      // Se não estava conectado e continua não conectado
      else if (!statusResponse.data.connected && currentStatus !== 'connecting') {
        // Verificar se tem QR code disponível - se tiver, deve estar em processo de conexão
        if (statusResponse.data.qrCode) {
          console.log(`[${timestamp}] QR code disponível mas status não é 'connecting', atualizando QR code`);
          setQrCodeImage(statusResponse.data.qrCode);
          // Não mudar para disconnected se há um QR code válido
          if (currentStatus !== 'connecting') {
            setStatus('connecting');
          }
        } else {
          setStatus('disconnected');
          stopStatusPolling();
        }
      }
    } catch (error) {
      const timestamp = new Date().toISOString();
      
      // Verificar se o erro foi por timeout ou cancelamento
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        console.warn(`[${timestamp}] Timeout ao verificar status do WhatsApp, tentando novamente na próxima iteração...`);
        return; // Não alterar o status neste caso, continuar polling
      }
      
      console.error(`[${timestamp}] Erro ao verificar status do WhatsApp:`, error);
      
      // Contagem de falhas consecutivas para evitar desconectar por erros temporários
      const currentFailCount = window.whatsappFailCount || 0;
      window.whatsappFailCount = currentFailCount + 1;
      
      // Aumentar tolerância a falhas de rede (7 tentativas)
      if (window.whatsappFailCount > 7 && status !== 'connected') {
        console.log(`[${timestamp}] Desconectando após ${window.whatsappFailCount} falhas consecutivas`);
        setStatus('disconnected');
        stopStatusPolling();
        window.whatsappFailCount = 0;
        
        toast({
          variant: "destructive",
          title: "Conexão perdida",
          description: "Não foi possível se comunicar com o servidor. Por favor, tente novamente."
        });
      } else if (status === 'connected') {
        console.log(`[${timestamp}] Mantendo status conectado apesar do erro temporário ${window.whatsappFailCount}/7`);
        // Quando conectado, ser muito mais tolerante a falhas de rede
      } else {
        console.log(`[${timestamp}] Erro ${window.whatsappFailCount}/7: Continuando polling`);
      }
    }
  }, [status, qrCodeImage, setStatus, stopStatusPolling, toast]);

  const handleConnect = async () => {
    // Prevenir inicialização dupla se já estiver conectando
    if (status === 'connecting') {
      console.log('Conexão já em andamento, ignorando solicitação duplicada');
      toast({
        title: t('whatsapp.messages.connectionInProgress.title'),
        description: t('whatsapp.messages.connectionInProgress.description')
      });
      return;
    }
    
    // Se já estiver conectado, verificar se a conexão está realmente ativa
    if (status === 'connected') {
      try {
        console.log('WhatsApp parece estar conectado, validando conexão...');
        const validationResponse = await api.get('/whatsapp/validate-connection');
        
        if (validationResponse.data.valid) {
          console.log('Conexão validada com sucesso, WhatsApp está realmente conectado.');
          toast({
            title: t('whatsapp.messages.alreadyConnected.title'),
            description: t('whatsapp.messages.alreadyConnected.description')
          });
          return;
        } else {
          console.log('Conexão inválida detectada, necessário reconectar.');
          // Continuar com a reconexão nesse caso
        }
      } catch (validationError) {
        console.error('Erro ao validar conexão:', validationError.message);
        // Continuar com a reconexão em caso de erro
      }
    }
    
    try {
      // Limpar qualquer QR code existente e definir status como connecting
      setQrCodeImage('');
      setStatus('connecting');
      
      // Mostrar toast para indicar início do processo
      toast({
        title: t('whatsapp.messages.initiatingConnection.title'),
        description: t('whatsapp.messages.initiatingConnection.description')
      });
      
      // Primeiro tente uma reconexão rápida se houver uma sessão prévia
      console.log('Verificando se é possível reconectar a sessão existente...');
      
      // Tentar refresh antes de iniciar nova sessão
      try {
        console.log('Solicitando refresh da sessão com timeout de 30 segundos');
        const refreshResponse = await api.post('/whatsapp/refresh-session', {}, {
          timeout: 40000 // Aumentar timeout para 40 segundos para processos mais lentos
        });
        console.log('Tentativa de reconexão rápida:', refreshResponse.data);
        
        // Se a reconexão foi bem-sucedida e já trouxe o status conectado
        if (refreshResponse.data.success && refreshResponse.data.isConnected) {
          console.log('Reconexão rápida bem-sucedida!');
          setStatus('connected');
          toast({
            title: "WhatsApp conectado",
            description: "Reconexão bem-sucedida."
          });
          return;
        }
        
        // Se a reconexão trouxe um QR code, mostrar
        if (refreshResponse.data.qrCode) {
          console.log('Reconexão trouxe um novo QR code');
          setQrCodeImage(refreshResponse.data.qrCode);
          // Exibir toast específico para escanear QR code
          toast({
            title: "QR Code disponível",
            description: "Por favor, escaneie o QR Code com seu WhatsApp."
          });
        }
      } catch (refreshError) {
        console.log('Erro na tentativa de reconexão rápida:', refreshError.message);
        // Se o erro for de conexão, informar ao usuário
        if (refreshError.message.includes('Network Error') || refreshError.message.includes('ECONNREFUSED')) {
          toast({
            variant: "destructive",
            title: "Erro de conexão",
            description: "Não foi possível conectar ao servidor. Verifique se o servidor está em execução."
          });
        }
        // Continuar com o processo normal de inicialização
      }
      
      console.log('Iniciando conexão completa com WhatsApp...');
      
      // Iniciar polling imediatamente para detectar mudanças de status
      // mesmo antes da resposta de inicialização
      startStatusPolling(2000);
      
      // Iniciamos a conexão em um bloco try/catch separado para não interromper o fluxo
      try {
        // Fazemos uma requisição simples para iniciar o processo sem esperar pela conclusão total
        const response = await api.post('/whatsapp/init', {}, {
          timeout: 30000 // Timeout de 30 segundos para sincronizar com o backend
        });
        
        console.log('Resposta inicial da inicialização:', response.data);
        if (response.data.qrCode) {
          setQrCodeImage(response.data.qrCode);
        }
        
        // Verificar se já está conectado imediatamente após iniciar
        if (response.data.isConnected) {
          console.log('Já está conectado na resposta inicial');
          setStatus('connected');
          stopStatusPolling();
          toast({
            title: "WhatsApp conectado",
            description: "Sua conta WhatsApp já estava vinculada."
          });
          return; // Retornar aqui para evitar o timeout de 2 minutos
        }
      } catch (initError) {
        // Se der erro ao iniciar, verificar se é necessário cancelar o processo
        console.log('Erro ao obter resposta inicial:', initError.message);
        
        // Verificar se temos um QR code no erro (caso parcial)
        if (initError.response?.data?.qrCode) {
          console.log('Recebido QR code mesmo com erro');
          setQrCodeImage(initError.response.data.qrCode);
        } 
        // Se o erro for crítico, interromper o processo
        else if (initError.response?.status >= 500) {
          console.error('Erro crítico no backend:', initError.response?.data);
          setStatus('disconnected');
          stopStatusPolling();
          toast({
            variant: "destructive",
            title: "Erro de conexão",
            description: "Houve um erro no servidor. Tente novamente em alguns instantes."
          });
          return;
        }
        
        // Continuar polling para outros tipos de erros
        console.log('Continuando polling apesar do erro inicial');
      }
      
      // Configurar um timeout para verificar se a conexão foi bem sucedida após 2 minutos
      connectTimeoutRef.current = setTimeout(async () => {
        // Somente verificar novamente se ainda estivermos em estado 'connecting'
        if (status === 'connecting') {
          console.log('Tempo limite de conexão, verificando status final...');
          try {
            const statusCheck = await api.get('/whatsapp/status');
            console.log('Status final após timeout:', statusCheck.data);
            
            if (statusCheck.data.connected) {
              console.log('Conexão bem-sucedida detectada no timeout final');
              setStatus('connected');
              stopStatusPolling();
              
              toast({
                title: "WhatsApp conectado",
                description: "Sua conta foi vinculada com sucesso."
              });
              return;
            }
            
            // Se realmente não está conectado após o timeout
            console.log('Falha na conexão após tempo limite');
            setStatus('disconnected');
            stopStatusPolling(); // Parar o polling se falhar a conexão
            
            toast({
              variant: "destructive",
              title: "Tempo expirado",
              description: "Não foi possível conectar ao WhatsApp. Tente novamente."
            });
          } catch (err) {
            console.error("Erro ao verificar status final:", err);
            setStatus('disconnected');
            stopStatusPolling();
          }
        }
      }, 120000);
      
    } catch (error) {
      console.error('Erro ao iniciar conexão WhatsApp:', error);
      
      // Verificar se foi um erro de timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.log('Timeout ao iniciar conexão, mas continuando polling...');
        
        // Mesmo com timeout, podemos verificar se o backend conseguiu iniciar a conexão
        toast({
          // Use o variant correto que o componente toast suporta
          variant: "default",
          title: "Inicialização lenta",
          description: "O processo está demorando. Continuando a verificação de conexão..."
        });
        
        // Continuar polling para verificar se a conexão foi estabelecida no backend
        startStatusPolling(2000);
      } else {
        // Para outros erros, realmente desconectar
        setStatus('disconnected');
        toast({
          variant: "destructive",
          title: "Erro de conexão",
          description: "Não foi possível iniciar a conexão com o WhatsApp."
        });
      }
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/whatsapp/logout');
      setStatus('disconnected');
      setQrCodeImage('');
      stopStatusPolling(); // Parar o polling ao desconectar manualmente
      
      toast({
        title: t('whatsapp.messages.disconnectedSuccess.title'),
        description: t('whatsapp.messages.disconnectedSuccess.description')
      });
    } catch (error) {
      console.error('Erro ao desconectar WhatsApp:', error);
      toast({
        variant: "destructive",
        title: t('whatsapp.errors.disconnectFailed.title'),
        description: t('whatsapp.errors.disconnectFailed.description')
      });
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    // Limitar a 5 arquivos
    if (selectedFiles.length + files.length > 5) {
      toast({
        variant: "destructive",
        title: t('whatsapp.errors.fileLimit.title'),
        description: t('whatsapp.errors.fileLimit.description')
      });
      return;
    }
    
    // Verificar tamanho dos arquivos (limite de 16MB cada - limite do WhatsApp)
    const invalidFiles = files.filter(file => file.size > 16 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      toast({
        variant: "destructive",
        title: t('whatsapp.errors.fileSize.title'),
        description: t('whatsapp.errors.fileSize.description')
      });
      return;
    }
    
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const toggleSelectAllBuyers = () => {
    if (selectedBuyers.length === buyers.length) {
      setSelectedBuyers([]);
    } else {
      setSelectedBuyers(buyers.map(buyer => buyer._id));
    }
  };

  const toggleSelectBuyer = (buyerId) => {
    setSelectedBuyers(prev => 
      prev.includes(buyerId)
        ? prev.filter(id => id !== buyerId)
        : [...prev, buyerId]
    );
  };

  const handlePreviewMessage = () => {
    // Criar uma versão de preview da mensagem com um comprador de exemplo
    if (buyers.length > 0) {
      const exampleBuyer = buyers[0];
      let preview = messageTemplate;
      
      // Substituir as variáveis de template com dados reais
      preview = preview
        .replace(/\{nome\}/g, exampleBuyer.nomeFantasia || t('whatsapp.preview.defaultClient'))
        .replace(/\{empresa\}/g, exampleBuyer.razaoSocial || exampleBuyer.nomeFantasia || t('whatsapp.preview.defaultCompany'))
        .replace(/\{cidade\}/g, exampleBuyer.endereco?.cidade || t('whatsapp.preview.defaultCity'));
      
      setPreviewMessage(preview);
      setIsDialogOpen(true);
    } else {
      toast({
        variant: "destructive",
        title: t('whatsapp.errors.noPreviewData.title'),
        description: t('whatsapp.errors.noPreviewData.description')
      });
    }
  };

  // Função para verificar se a conexão está válida e reconectar se necessário
  const validateConnection = async () => {
    try {
      // Verificar primeiro o status interno
      if (status !== 'connected') {
        toast({
          variant: "destructive",
          title: t('whatsapp.errors.notConnected.title'),
          description: t('whatsapp.errors.notConnected.description')
        });
        return false;
      }
      
      console.log('Verificando status atual no backend...');
      
      // Verificar status no backend com retry automático
      let statusCheck;
      try {
        statusCheck = await api.get('/whatsapp/status', {
          timeout: 8000 // Aumentado para dar mais tempo ao backend
        });
        
        console.log('Status no backend:', statusCheck.data);
      } catch (statusError) {
        console.error('Erro ao verificar status:', statusError);
        // Tentar uma segunda vez em caso de erro
        try {
          console.log('Tentando verificação de status novamente após erro...');
          await new Promise(resolve => setTimeout(resolve, 1000)); // Aguardar 1 segundo
          statusCheck = await api.get('/whatsapp/status', {
            timeout: 8000
          });
        } catch (retryError) {
          console.error('Erro na segunda tentativa de verificar status:', retryError);
          setStatus('disconnected');
          toast({
            variant: "destructive",
            title: "Erro de conexão",
            description: "Não foi possível verificar o status da conexão. Tente novamente."
          });
          return false;
        }
      }
      
      // Verificar detalhes do status para diagnóstico
      if (statusCheck.data.validationStatus && !statusCheck.data.validationStatus.valid) {
        console.log(`Validação falhou: ${statusCheck.data.validationStatus.message}`);
      }
      
      // Se não está conectado, atualizar o status e retornar false
      if (!statusCheck.data.connected) {
        console.log('Verificação de conexão: backend reporta desconectado');
        setStatus('disconnected');
        toast({
          variant: "destructive",
          title: "WhatsApp desconectado",
          description: "A conexão com o WhatsApp foi perdida. Por favor, reconecte."
        });
        return false;
      }
      
      // Se está conectado no status, verificar se a conexão é realmente válida
      console.log('Verificando validade da conexão com WhatsApp...');
      try {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] Validando conexão com timeout de 20 segundos`);
        
        const response = await api.get('/whatsapp/validate-connection', {
          timeout: 20000 // Aumentado para 20 segundos para garantir tempo suficiente
        });
        
        if (!response.data.valid) {
          // Conexão expirada, mas ainda marcada como conectada
          console.log(`[${timestamp}] Conexão considerada inválida pelo backend, tentando reconectar...`);
          console.log(`[${timestamp}] Detalhes da validação:`, response.data);
          
          // Mostrar mensagem mais informativa
          toast({
            title: "Reconectando WhatsApp",
            description: response.data.message || "A sessão expirou. Tentando reconectar automaticamente...",
            duration: 5000,
          });
          
          // Tentar reiniciar a sessão - com tratamento de erro
          try {
            console.log('Solicitando refresh da sessão...');
            await api.post('/whatsapp/refresh-session', {}, {
              timeout: 15000 // Aumentado para dar mais tempo ao refresh
            });
            
            // Aguardar um pouco para o backend processar
            console.log('Aguardando 3 segundos após o refresh...');
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            // Verificar se a reconexão foi bem-sucedida
            console.log('Verificando status após refresh...');
            const statusResp = await api.get('/whatsapp/status');
            
            if (!statusResp.data.connected) {
              // Se não conseguiu reconectar, precisa de QR Code novamente
              console.log('Reconexão falhou, necessário novo QR code');
              setStatus('disconnected');
              toast({
                variant: "destructive",
                title: "Reconexão falhou",
                description: "Por favor, escaneie o QR Code novamente para reconectar.",
              });
              return false;
            } else {
              console.log('Reconexão bem-sucedida!');
              toast({
                title: "WhatsApp reconectado",
                description: "Conexão restabelecida com sucesso.",
              });
              return true;
            }
          } catch (refreshError) {
            console.error('Erro durante o refresh da sessão:', refreshError);
            setStatus('disconnected');
            toast({
              variant: "destructive",
              title: "Falha na reconexão",
              description: "Não foi possível reestabelecer a conexão. Por favor, tente conectar novamente.",
            });
            return false;
          }
        }
      } catch (validationError) {
        console.error('Erro ao validar conexão:', validationError);
        // Se houver erro ao validar, consideramos que a conexão está quebrada
        setStatus('disconnected');
        toast({
          variant: "destructive",
          title: "Erro de conexão",
          description: "Não foi possível validar a conexão. Por favor, reconecte.",
        });
        return false;
      }
      // Se chegou até aqui, a conexão está válida
      console.log('Conexão validada com sucesso');
      return true;
    } catch (error) {
      console.error('Erro ao validar conexão:', error);
      setStatus('disconnected');
      toast({
        variant: "destructive",
        title: "Erro de conexão",
        description: "Não foi possível validar a conexão. Tente conectar novamente."
      });
      return false;
    }
  };

  const handleSendMessages = async () => {
    if (selectedBuyers.length === 0) {
      toast({
        variant: "destructive",
        title: t('whatsapp.errors.noRecipients.title'),
        description: t('whatsapp.errors.noRecipients.description')
      });
      return;
    }

    if (!messageTemplate.trim()) {
      toast({
        variant: "destructive", 
        title: t('whatsapp.errors.emptyMessage.title'),
        description: t('whatsapp.errors.emptyMessage.description')
      });
      return;
    }

    try {
      setLoading(true);
      
      // Validar conexão antes de enviar
      const isConnected = await validateConnection();
      if (!isConnected) {
        setLoading(false);
        return;
      }
      
      setSendingProgress({ current: 0, total: selectedBuyers.length });
      
      // Preparar dados para envio
      const formData = new FormData();
      formData.append('message', messageTemplate);
      formData.append('buyers', JSON.stringify(selectedBuyers));
      
      // Adicionar arquivos se existirem
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      // Enviar mensagem para todos os compradores selecionados
      const response = await api.post('/whatsapp/send-bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      
      toast({
        title: t('whatsapp.messages.messagesSent.title'),
        description: t('whatsapp.messages.messagesSent.description', { count: response.data.successCount })
      });
      
      // Resetar estado após envio bem-sucedido
      setSelectedFiles([]);
      setSelectedBuyers([]);
      
    } catch (error) {
      console.error('Erro ao enviar mensagens:', error);
      toast({
        variant: "destructive",
        title: t('whatsapp.errors.sendFailed.title'),
        description: error.response?.data?.message || t('whatsapp.errors.sendFailed.description')
      });
    } finally {
      setLoading(false);
      setSendingProgress({ current: 0, total: 0 });
    }
  };

  // Função para formatar número de telefone
  const formatPhone = (phone) => {
    if (!phone) return '';
    
    // Remover caracteres não numéricos
    const numbers = phone.replace(/\D/g, '');
    
    if (numbers.length === 11) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7)}`;
    } else if (numbers.length === 10) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 6)}-${numbers.substring(6)}`;
    }
    return phone;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <PageHeader
        title={t('whatsapp.title')}
        description={t('whatsapp.description')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Coluna de status/conexão */}
        <div className="lg:col-span-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-[1.1rem]">
                {t('whatsapp.connectionStatus.title')}
              </CardTitle>
              <CardDescription>
                {t('whatsapp.connectionStatus.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <div 
                  className={`h-3 w-3 rounded-full ${
                    status === 'connected' ? 'bg-green-500' : 
                    status === 'connecting' ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                />
                <span className="font-medium">
                  {status === 'connected' ? t('whatsapp.connectionStatus.connected') : 
                   status === 'connecting' ? t('whatsapp.connectionStatus.connecting') : t('whatsapp.connectionStatus.disconnected')}
                </span>
              </div>

              {status === 'connected' && (
                <Alert>
                  <Check className="h-4 w-4" />
                  <AlertTitle>{t('whatsapp.connectionStatus.connectedAlert.title')}</AlertTitle>
                  <AlertDescription>
                    {t('whatsapp.connectionStatus.connectedAlert.description')}
                  </AlertDescription>
                </Alert>
              )}

              {status === 'connecting' && qrCodeImage && (
                <div className="flex flex-col items-center justify-center space-y-4 p-4">
                  <div className="border border-gray-200 p-4 rounded-lg bg-white">
                    <img 
                      src={`data:image/png;base64,${qrCodeImage}`}
                      alt="QR Code WhatsApp"
                      className="w-48 h-48"
                    />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">
                    {t('whatsapp.qrCode.scanInstruction')}
                  </p>
                  <p className="text-xs text-center text-amber-500">
                    {t('whatsapp.qrCode.warning')}
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter>
              {status === 'connected' ? (
                <Button 
                  variant="destructive" 
                  onClick={handleDisconnect}
                  className="w-full"
                >
                  {t('whatsapp.buttons.disconnect')}
                </Button>
              ) : (
                <Button 
                  onClick={handleConnect}
                  disabled={status === 'connecting'}
                  className="w-full"
                >
                  {status === 'connecting' ? t('whatsapp.buttons.connecting') : t('whatsapp.buttons.connect')}
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>

        {/* Coluna de envio de mensagens */}
        <div className="lg:col-span-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[1.1rem]">
                <Send className="h-5 w-5" />
                {t('whatsapp.messaging.title')}
              </CardTitle>
              <CardDescription>
                {t('whatsapp.messaging.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs defaultValue="message">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="message">{t('whatsapp.tabs.message')}</TabsTrigger>
                  <TabsTrigger value="recipients">{t('whatsapp.tabs.recipients')}</TabsTrigger>
                </TabsList>
                
                <TabsContent value="message" className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="message-template">{t('whatsapp.messageTemplate.label')}</Label>
                    <Textarea 
                      id="message-template"
                      placeholder={t('whatsapp.messageTemplate.placeholder')}
                      rows={6}
                      value={messageTemplate}
                      onChange={(e) => setMessageTemplate(e.target.value)}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('whatsapp.messageTemplate.variablesHelp')}
                    </p>
                    <div className="flex justify-end">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handlePreviewMessage}
                      >
                        {t('whatsapp.buttons.preview')}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>{t('whatsapp.files.label')}</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="text-xs text-muted-foreground">
                              {t('whatsapp.files.limits')}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('whatsapp.files.tooltip')}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    
                    {selectedFiles.length > 0 ? (
                      <div className="space-y-2">
                        {selectedFiles.map((file, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-2 border rounded-md bg-muted/20"
                          >
                            <div className="flex items-center space-x-2">
                              <FileSpreadsheet className="h-4 w-4 text-blue-600" />
                              <div className="text-sm truncate max-w-[300px]">
                                {file.name}
                                <span className="text-xs text-muted-foreground ml-1">
                                  ({Math.round(file.size / 1024)} KB)
                                </span>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => removeFile(index)}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        
                        {selectedFiles.length < 5 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => fileInputRef.current.click()}
                          >
                            <Plus className="h-4 w-4 mr-1" /> {t('whatsapp.files.addMore')}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div 
                        onClick={() => fileInputRef.current.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground mb-1">
                          {t('whatsapp.files.dropzone.title')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('whatsapp.files.dropzone.subtitle')}
                        </p>
                      </div>
                    )}
                    
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      ref={fileInputRef}
                      className="hidden"
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="recipients" className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Switch 
                        id="select-all" 
                        checked={selectedBuyers.length === buyers.length && buyers.length > 0} 
                        onCheckedChange={toggleSelectAllBuyers}
                      />
                      <Label htmlFor="select-all" className="cursor-pointer">
                        {t('whatsapp.recipients.selectAll', { count: buyers.length })}
                      </Label>
                    </div>
                    <Badge variant="outline">
                      {t('whatsapp.recipients.selected', { count: selectedBuyers.length })}
                    </Badge>
                  </div>

                                      <div className="border rounded-md overflow-hidden">
                    <div className="p-2 bg-muted font-medium text-sm">
                      {t('whatsapp.recipients.listTitle')}
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {loading ? (
                        <div className="p-8 text-center">
                          <p className="text-sm text-muted-foreground">{t('whatsapp.recipients.loading')}</p>
                        </div>
                      ) : buyers.length > 0 ? (
                        <div className="divide-y">
                          {buyers.map((buyer) => (
                            <div 
                              key={buyer._id} 
                              className="p-3 flex items-center justify-between hover:bg-muted/50"
                            >
                              <div className="flex-1">
                                <p className="font-medium">{buyer.nomeFantasia}</p>
                                <p className="text-sm text-muted-foreground">
                                  {formatPhone(buyer.contatoPrincipal?.celular || buyer.contatoPrincipal?.telefone)}
                                </p>
                              </div>
                              <Switch 
                                checked={selectedBuyers.includes(buyer._id)}
                                onCheckedChange={() => toggleSelectBuyer(buyer._id)}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center">
                          <p className="text-sm text-muted-foreground">
                            {t('whatsapp.recipients.noBuyers')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button 
                disabled={status !== 'connected' || 
                         loading || 
                         selectedBuyers.length === 0 || 
                         !messageTemplate.trim()}
                onClick={handleSendMessages}
              >
                {loading ? t('whatsapp.buttons.sending') : t('whatsapp.buttons.sendMessages')}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Dialog de preview */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('whatsapp.preview.title')}</DialogTitle>
            <DialogDescription>
              {t('whatsapp.preview.description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-muted p-4 rounded-md whitespace-pre-line">
            {previewMessage}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setIsDialogOpen(false)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de progresso de envio (aparece quando está enviando) */}
      {loading && sendingProgress.total > 0 && (
        <Dialog open={true} onOpenChange={() => {}}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('whatsapp.sendingProgress.title')}</DialogTitle>
              <DialogDescription>
                {t('whatsapp.sendingProgress.description')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary" 
                  style={{ 
                    width: `${(sendingProgress.current / sendingProgress.total) * 100}%` 
                  }}
                />
              </div>
              
              <p className="text-center text-sm">
                {t('whatsapp.sendingProgress.progress', { 
                  current: sendingProgress.current, 
                  total: sendingProgress.total 
                })}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </motion.div>
  );
}
